// src/contexts/WebSearchResultsContext.tsx

import React, { createContext, useContext, useMemo } from "react";
import { WebSearchEvent } from "../interfaces/webSearchTypes";
import { WebFetchToolEvent } from "../interfaces/webToolTypes";

/**
 * Context for looking up web search result titles by URL.
 * Used by CitationBubble to resolve actual article titles for tooltip display.
 */
interface WebSearchResultsContextType {
  /** Look up article title by URL. Returns null if not found. */
  lookupTitleByUrl: (url: string) => string | null;
}

const WebSearchResultsContext = createContext<WebSearchResultsContextType | undefined>(undefined);

interface WebSearchResultsProviderProps {
  children: React.ReactNode;
  webSearchEvents: WebSearchEvent[];
  webFetchEvents?: WebFetchToolEvent[];
}

/**
 * Provider that builds a URL→Title lookup map from web search events.
 * Wrap around MarkdownRenderer content so CitationBubble can access titles.
 */
export const WebSearchResultsProvider: React.FC<WebSearchResultsProviderProps> = ({
  children,
  webSearchEvents,
  webFetchEvents,
}) => {
  // Build URL→Title map from all search results
  const urlToTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of webSearchEvents) {
      for (const result of event.results) {
        if (result.url && result.title && !map.has(result.url)) {
          map.set(result.url, result.title);
        }
      }
    }
    // Also index web fetch page titles
    if (webFetchEvents) {
      for (const event of webFetchEvents) {
        if (event.url && event.title && !map.has(event.url)) {
          map.set(event.url, event.title);
        }
      }
    }
    return map;
  }, [webSearchEvents, webFetchEvents]);

  const lookupTitleByUrl = useMemo(() => {
    return (url: string): string | null => {
      return urlToTitleMap.get(url) ?? null;
    };
  }, [urlToTitleMap]);

  return (
    <WebSearchResultsContext.Provider value={{ lookupTitleByUrl }}>
      {children}
    </WebSearchResultsContext.Provider>
  );
};

/**
 * Hook to access web search results lookup.
 * Returns a no-op lookup if used outside provider (graceful fallback).
 */
export const useWebSearchResults = (): WebSearchResultsContextType => {
  const context = useContext(WebSearchResultsContext);
  // Return no-op lookup if not within provider (fallback behavior)
  if (!context) {
    return { lookupTitleByUrl: () => null };
  }
  return context;
};

export default WebSearchResultsContext;
